<template>
  <div class="p-6 print:p-0">
    <!-- COVER PAGE -->
    <template v-if="view === 'workbook' && !singleProductExport">
      <header
          :style="coverPage?.meta.image ? { backgroundImage: `url(${bgImageUrl})` } : {}"
          class="workbook-category-intro bg-cover bg-center wb-intro--page wb-intro--print bg-gray-400"                    
        >
        <div
          v-if="phase !== 'ready' || !auth.hydrated"
          class="flex items-center justify-center"
        >
          <div class="text-sm text-neutral-400">Laddar…</div>
        </div>
        <div v-else>
          <UPage>
            <UContainer class="ucontainer flex flex-col justify-evenly h-[2150px]">
              <div class="flex flex-col items-center w-full">
                <Logo class="w-1/2 lg:w-96" />
              </div>
              <div class="text-center text-white w-full flex items-center justify-center flex-col">
                
                <ContentRenderer class="" v-if="coverPage" :value="coverPage" />
                <div v-else>Print-cover.md not found</div>
              </div>
            </UContainer>
          </UPage>
        </div>
      </header>
    </template>
    
    <!-- REST OF PRINT PAGE -->
    <header class="flex items-start justify-between mb-6 print:mb-4" v-if="view === 'overview'">
      <Logo :isDark="true" class="h-10 w-auto" />
      <ClientOnly>
        <div class="text-right text-xs leading-tight text-neutral-600">
          <template v-if="view === 'overview'">
            Kudo Sales demo Line Overview<br />
            {{ dateString }}
          </template>
          <template v-else>
            Kudo Sales demo Workbook 
            {{ dateString }}
          </template>
        </div>
      </ClientOnly>
    </header>
    <div v-if="isLoading" class="py-24 text-center text-lg text-gray-400">
      Loading products...
    </div>
    <div v-if="view === 'overview'">
      <WorkbookLineOverview :categories="filteredCategories" is-print />
    </div>
    <div v-else>
      <template v-if="workbookCategories.length">
        <div v-for="cat in workbookCategories" :key="cat.Slug">
          <!-- 1. CATEGORY INTRO BLOCK -->
          
          <CategoryIntro
            v-if="categoryPages[cat.Slug] && !singleProductExport"
            :image="categoryPages[cat.Slug]?.meta?.image"            
            :category="cat.Name">
            <ContentRenderer
              v-if="categoryIntroPages[cat.Slug]"
              :value="categoryIntroPages[cat.Slug]"
            />
            </CategoryIntro>
          
           
          <div
            v-for="pm in cat.ProductModels"
            :key="pm.Style"
            class="workbook-model-sheet"
          >
            
            <WorkbookProductDisplay :model="pm" :is-print="true" :show-cart-table="showCartTable" />
          </div>
        </div>
      </template>
      <div v-else class="py-24 text-center text-lg text-gray-400">
        No models match the current criteria.
      </div>
      
      <!-- BACK COVER PAGE (contact page in workbook view) -->
      <template v-if="view === 'workbook' && backCover">
        <header
          :style="backCover?.meta?.image ? { backgroundImage: `url(${contactBgImageUrl})` } : {}"
          class="workbook-category-intro bg-cover bg-center wb-intro--page wb-intro--print bg-workbook-orange"
        >
          <UPage>
            <UContainer class="ucontainer flex flex-col justify-evenly h-[2150px]">
              <div class="flex flex-col items-center w-full">
                <Logo class="w-1/2 lg:w-36 pt-32" />
              </div>
              <div class="text-center text-white w-full flex items-center justify-center flex-col">
                <ContentRenderer v-if="backCover" :value="backCover" />
                <div v-else>Contact not found</div>
              </div>
            </UContainer>
          </UPage>
        </header>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useCatalogStore } from '@/store/catalog'
import { useSettingsStore } from '@/store/settings'
import { useAuthStore } from '@/store/auth'
import { useCartStore } from '@/store/cart'
import type { CatalogItem, ProductModel, CategoryModel } from '@/types/catalog'
import WorkbookLineOverview from '@/components/workbook/LineOverview.vue'
import WorkbookProductDisplay from '@/components/workbook/ProductDisplay.vue'

import Logo from '@/components/logo.vue'
import { toTwicpicBgUrl } from '@/utils/productImages'
import CategoryIntro from '~/components/content/CategoryIntro.vue'


import { normalizeSizeCode } from '@/utils/sizes'
import { useRuntimeConfig, useCookie } from '#app'
const config = useRuntimeConfig()

const components = { CategoryIntro }

definePageMeta({ layout: 'headless' })

const route = useRoute()
const catalogStore = useCatalogStore()
const settings = useSettingsStore()
const auth = useAuthStore()
settings.hydrate()
auth.hydrated = true

// Hydrate cart from window.__PDF_CARTDATA__ (for PDF generation), fallback to cookie/localStorage
if (process.client) {
  const cartStore = useCartStore()
  let cartdataString: string | null = null

  if (typeof window !== 'undefined' && window.__PDF_CARTDATA__) {
    cartdataString = window.__PDF_CARTDATA__
  } else {
    // Fallback to cookie
    const cartdataCookie = useCookie('cartdata')
    if (cartdataCookie.value) {
      cartdataString = cartdataCookie.value
    } else {
      // Fallback to localStorage
      cartdataString = localStorage.getItem('cartdata')
    }
  }

  if (cartdataString) {
    try {
      const decoded = atob(String(cartdataString))
      const parsed = JSON.parse(decoded)
      if (parsed.saved) {
        cartStore.savedStyles = new Set(parsed.saved)
      }
      if (parsed.items) {
        for (const [sku, obj] of Object.entries(parsed.items)) {
          cartStore.lineItems[sku] = {
            sku,
            style: '',
            colour: '',
            size: '',
            qty: (obj as any).qty || 0
          }
        }
      }
      cartStore.hydrated = true
      console.log('[print.vue] Hydrated cart from PDF window/cookie', { saved: cartStore.savedStyles.size, items: Object.keys(cartStore.lineItems).length })
    } catch (err) {
      console.error('[print.vue] Failed to parse cartdata:', err)
      cartStore.hydrate() // Fallback to localStorage
    }
  } else {
    cartStore.hydrate() // Fallback to localStorage
  }
  
  // Hydrate notes similarly
  const { useNotesStore } = await import('@/store/notes')
  const notesStore = useNotesStore()
  let notesdataString: string | null = null

  if (typeof window !== 'undefined' && window.__PDF_NOTESDATA__) {
    notesdataString = window.__PDF_NOTESDATA__
  } else {
    // Fallback to cookie
    const notesdataCookie = useCookie('notesdata')
    if (notesdataCookie.value) {
      notesdataString = notesdataCookie.value
    } else {
      // Fallback to localStorage
      notesdataString = localStorage.getItem('notes-v1')
    }
  }

  if (notesdataString) {
    try {
      let parsed
      // Check if it's base64 encoded (from cookie/window) or plain JSON (from localStorage)
      if (notesdataString.startsWith('{')) {
        parsed = JSON.parse(notesdataString)
      } else {
        const decoded = atob(String(notesdataString))
        parsed = JSON.parse(decoded)
      }
      if (parsed.notes) {
        notesStore.notes = parsed.notes
      }
      notesStore.hydrated = true
      console.log('[print.vue] Hydrated notes from PDF window/cookie/localStorage', { count: Object.keys(notesStore.notes).length })
    } catch (err) {
      console.error('[print.vue] Failed to parse notesdata:', err)
      notesStore.hydrate() // Fallback to localStorage
    }
  } else {
    notesStore.hydrate() // Fallback to localStorage
  }
}

if (
  typeof route.query.currency === 'string' &&
  route.query.currency &&
  route.query.currency !== settings.currency
) {
  settings.setCurrency(route.query.currency)
}

const dateString = new Date().toLocaleDateString()
const view = computed<'overview' | 'workbook'>(() =>
  route.query.view === 'workbook' ? 'workbook' : 'overview'
)
const singleProductExport = computed(() => {
  const raw = route.query.singleProduct
  if (Array.isArray(raw)) {
    return raw.some(v => v === '1' || v === 'true')
  }
  return raw === '1' || raw === 'true'
})
const topCategory = computed(() => {
  const raw = String(route.query.mode ?? '')
  if (!raw || raw === 'All') return ''
  const match = catalogStore.topCategories.find(cat => cat.toLowerCase() === raw.toLowerCase())
  return match ?? raw
})
const isLoading = computed(
  () => catalogStore.isLoading || !catalogStore.items.length
)

// --- COVER PAGE DATA (from index.vue) ---
const { data: coverPage } = await useAsyncData('content:cover-page', () =>
  queryCollection('cover').first()
)

const { data: backCover } = await useAsyncData('content:back-cover-page', () =>
  queryCollection('backcover').first()
)

const phase = ref<'ssr' | 'hydrating' | 'ready'>('ready')

// NYTT: Samma logik som CategoryIntro.vue
const bgImageUrl = computed(() =>
  coverPage.value?.meta.image ? toTwicpicBgUrl(coverPage.value.meta.image + '?deployment=' + config.public.appVersion , true) : undefined
)



const contactBgImageUrl = computed(() =>
  backCover.value?.meta?.image ? toTwicpicBgUrl(backCover.value.meta.image + '?deployment=' + config.public.appVersion, true) : undefined
)

// --- CART DATA FOR ORDER SUMMARY ---
const cartStore = useCartStore()
const currency = computed(() => settings.currency)

function wsFieldForCurrency(cur: string): string {
  switch (cur) {
    case 'USD': return 'WSUSD'
    case 'EUR': return 'WSEUR'
    case 'GBP': return 'WSGBP'
    case 'CHF': return 'WSCHF'
    case 'CAD':
    default:
      return 'WSCAD'
  }
}

const orderDisplayRows = computed(() => {
  const rows: any[] = []
  const priceField = wsFieldForCurrency(currency.value)
  
  for (const [sku, lineItem] of Object.entries(cartStore.lineItems)) {
    if (lineItem.qty <= 0) continue
    
    const variant = catalogStore.items.find(v => v.SKU === sku)
    if (!variant) continue
    
    const model = catalogStore.modelByStyle(variant.Style)
    if (!model) continue
    
    const price = Number(variant?.[priceField] ?? model?.[priceField] ?? 0)
    
    rows.push({
      modelName: model?.name ?? '',
      style: variant?.Style ?? '',
      colour: variant?.Colour ?? '',
      size: normalizeSizeCode(variant?.SizeCode ?? variant?.Size ?? ''),
      sku: lineItem.sku,
      qty: lineItem.qty,
      totalPrice: price * lineItem.qty
    })
  }
  
  return rows
})

const hasOrderItems = computed(() => orderDisplayRows.value.length > 0)


// --- FILTERS + category selection logic (unchanged) ---
function qArr(key: string): string[] {
  const v = route.query[key]
  return Array.isArray(v) ? v.map(String) : v ? [String(v)] : []
}
function qStyles(): (string | number)[] {
  const raw = route.query.styles
  if (!raw) return []
  if (Array.isArray(raw)) {
    return raw.flatMap(r => r.split(',')).map(s => s.trim()).filter(Boolean)
  }
  return String(raw).split(',').map(s => s.trim()).filter(Boolean)
}
const filters = computed(() => ({
  category: qArr('category'),
  gender: qArr('gender'),
  productPrimaryUse: qArr('productPrimaryUse'),
  productFit: qArr('productFit'),
  status: qArr('status')
}))
function anyActiveFilters(): boolean {
  return Object.values(filters.value).some(a => a.length)
}
const statusCheckers: Record<string, (v: CatalogItem) => boolean> = {
  'New': v => !!v.New,
  'Revised': v => !!v.Revised,
  'New Colour': v => !!v.NewColour
}
function variantPasses(v: CatalogItem): boolean {
  if (filters.value.gender.length && !filters.value.gender.includes(v.Gender)) return false
  if (filters.value.productPrimaryUse.length && !filters.value.productPrimaryUse.includes(v.ProductPrimaryUse)) return false
  if (filters.value.productFit.length && !filters.value.productFit.includes(v.ProductFit)) return false
  if (filters.value.status.length) {
    const ok = filters.value.status.some(lbl => statusCheckers[lbl]?.(v))
    if (!ok) return false
  }
  return true
}
function modelIncluded(m: ProductModel): boolean {
  return m.variants.some(variantPasses)
}
const topCategoryCategories = computed<CategoryModel[]>(() =>
  catalogStore.categoriesByTopCategory(topCategory.value)
)
const filteredCategories = computed<CategoryModel[]>(() => {
  if (isLoading.value) return []
  
  const baseCats: CategoryModel[] = topCategoryCategories.value
  
  if (qStyles().length) {
    const styleSet = new Set(qStyles().map(String))
    const out: CategoryModel[] = []
    for (const cat of baseCats) {
      const kept: ProductModel[] = cat.ProductModels.filter(m => styleSet.has(String(m.Style)))
      if (kept.length) out.push({ ...cat, ProductModels: kept })
    }
    return out
  } else {
    const out: CategoryModel[] = []
    for (const cat of baseCats) {
      if (filters.value.category.length && !filters.value.category.includes(cat.Name)) continue
      const kept: ProductModel[] = []
      for (const m of cat.ProductModels) {
        if (modelIncluded(m)) kept.push(m)
      }
      if (kept.length) out.push({ ...cat, ProductModels: kept })
    }
    return out
  }
})

// --- SELECTED CATEGORIES for workbook ---
const selectedCategorySlugs = computed<string[]>(() => {
  const raw = route.query.categories ?? route.query.category ?? ''
  if (!raw) return catalogStore.categories.map(c => c.Slug)
  const list = (Array.isArray(raw) ? raw.flatMap(r => String(r).split(',')) : String(raw).split(','))
    .map(s => s.trim())
    .filter(Boolean)
  const mapped = list.map(val => {
    const match = catalogStore.categories.find(c => c.Slug === val || c.Name.toLowerCase() === val.toLowerCase())
    return match ? match.Slug : val
  })
  return Array.from(new Set(mapped))
})
const isFilteredWorkbook = computed(
  () => view.value === 'workbook' && route.query.filtered === '1' && anyActiveFilters()
)
const workbookCategories = computed<CategoryModel[]>(() => {
  if (isLoading.value || view.value !== 'workbook') return []
  
  // First, select the base categories based on top category
  let baseCats: CategoryModel[] = topCategoryCategories.value
  
  if (qStyles().length) {
    const styleSet = new Set(qStyles().map(String))
    const out: CategoryModel[] = []
    for (const cat of baseCats) {
      const kept: ProductModel[] = cat.ProductModels.filter(m => styleSet.has(String(m.Style)))
      if (kept.length) out.push({ ...cat, ProductModels: kept })
    }
    return out
  }
  const base = baseCats.filter(c => selectedCategorySlugs.value.includes(c.Slug))
  const baseAfterCategoryFilter = base.filter(cat => {
    if (filters.value.category.length && !filters.value.category.includes(cat.Name)) return false
    return true
  })
  if (!isFilteredWorkbook.value) return baseAfterCategoryFilter
  return baseAfterCategoryFilter
    .map(cat => {
      const kept: ProductModel[] = []
      for (const m of cat.ProductModels) {
        if (modelIncluded(m)) kept.push(m)
      }
      return { ...cat, ProductModels: kept }
    })
    .filter(c => c.ProductModels.length)
})

// NYTT: Flagga som är true om vi gör "current cart export" (dvs styles-param finns och cart=1 finns)
const showCartTable = computed(() => view.value === 'workbook' && route.query.cart === '1')

// --- LOAD ALL CATEGORY PAGES ---
const categoryPages = ref<Record<string, any>>({})

watchEffect(async () => {
  const slugs = workbookCategories.value.map(cat => cat.Slug)
  const result: Record<string, any> = {}
  await Promise.all(
    slugs.map(async slug => {
      const { data: page } = await useAsyncData(
        'product-print-page-' + slug,
        () => queryCollection('products').path('/products/' + slug).first()
      )
      result[slug] = page.value
    })
  )
  categoryPages.value = result
})

const categoryIntroPages = computed<Record<string, any>>(() => {
  const output: Record<string, any> = {}
  for (const slug of Object.keys(categoryPages.value)) {
    const page = categoryPages.value[slug]
    const body = page?.body?.value
    if (!Array.isArray(body)) continue
    // category-intro node structure within body: [ "category-intro", attrsObj, ...children ]
    const introNode = body.find(
      (node: unknown): node is [string, Record<string, unknown>, ...unknown[]] =>
        Array.isArray(node) &&
        node.length >= 2 &&
        typeof node[0] === 'string' &&
        node[0] === 'category-intro' &&
        typeof node[1] === 'object' &&
        node[1] !== null
    )
    if (!introNode) continue
    const children = introNode.slice(2)
    if (!children.length) continue
    output[slug] = {
      ...page,
      body: {
        ...page.body,
        value: children
      }
    }
  }
  return output
})


</script>

<style scoped>
.workbook-model-sheet {
  padding-bottom: 2rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid rgba(0,0,0,0.08);
}

.print-cover-page {
  page-break-after: always;
  break-after: page;
  border: none;
  margin: 0;
  padding: 0;
  min-height: 100vh;
}

@media print {
  .print-cover-page {
    page-break-after: always;
    break-after: page;
    border: none;
    margin: 0 0 1mm 0;
    padding: 0 0 2mm 0;
    min-height: 100vh;
    background: inherit !important;
  }
  .print-break {
    page-break-after: always;
    break-after: page;
  }
  .workbook-model-sheet {
    page-break-after: always;
    break-after: page;
    border: none;
    margin: 0 0 1mm 0;
    padding: 0 0 2mm 0;
  }
  h1, h2, h3 {
    break-after: avoid;
    page-break-after: avoid;
  }
}
</style>
